import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Search, 
  FileText, 
  Share2, 
  Palette, 
  Megaphone,
  Facebook,
  BarChart3,
  Target,
  Link2,
  Globe,
  Wand2,
  Copy,
  Calendar,
  BookOpen,
  Hash,
  Instagram,
  Twitter,
  Linkedin,
  TrendingUp,
  Users,
  Layout,
  Image,
  Type,
  Layers,
  Droplet,
  FileImage,
  Scissors,
  Lock,
  RotateCw,
  Settings,
  Sparkles,
  Eye,
  ScanText,
  Table,
  Zap,
  PenTool,
  Files,
  Crown,
  Star,
  Filter,
  Flame,
  Bot,
  MessageSquare,
  Languages,
  FileCode,
  Smartphone,
  Code,
  Rss,
  Map,
  History,
  Unlock,
  Stamp,
  Highlighter,
  FolderOpen,
  Merge,
  Video,
  Subtitles,
  PlayCircle,
  Youtube,
  Plus
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ToolSEOContent from "@/components/ToolSEOContent";
import WhatsAppChat from "@/components/WhatsAppChat";

export default function AllToolsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all"); // all, free, pro
  const [searchQuery, setSearchQuery] = useState("");

  const toolCategories = [
    {
      name: "YouTube CRM (Studio)",
      description: "Run unlimited YouTube channels — pipeline, team, AI scripts & SEO",
      icon: <Youtube className="w-6 h-6" />,
      color: "bg-red-100 text-red-700",
      borderColor: "border-red-200",
      path: "/studio",
      tools: [
        { name: "Studio Dashboard", icon: <Youtube />, path: "/studio", tier: "free", popular: true, isNew: true },
        { name: "Connect a Channel", icon: <Plus />, path: "/studio/channels", tier: "free", isNew: true },
        { name: "My Tasks", icon: <Sparkles />, path: "/studio/my-tasks", tier: "free", isNew: true },
        { name: "AI SEO Assistant", icon: <Sparkles />, path: "/studio/seo", tier: "free", isNew: true },
        { name: "AI Topic Suggestions", icon: <Sparkles />, path: "/studio/topic-suggestions", tier: "free", isNew: true },
      ]
    },
    {
      name: "Video Tools",
      description: "AI-powered video captions, transcripts & brand-ready exports",
      icon: <Video className="w-6 h-6" />,
      color: "bg-fuchsia-100 text-fuchsia-700",
      borderColor: "border-fuchsia-200",
      path: "/tools/auto-captions",
      tools: [
        { name: "AutoCaptions", icon: <Subtitles />, path: "/tools/auto-captions", tier: "free", popular: true, isNew: true },
        { name: "My Captioned Videos", icon: <PlayCircle />, path: "/tools/auto-captions/history", tier: "free", isNew: true },
        { name: "Brand Kit (Agency)", icon: <Sparkles />, path: "/tools/auto-captions/brand-kit", tier: "pro", isNew: true },
        { name: "SRT Captions Generator", icon: <Subtitles />, path: "/tools/srt-captions", tier: "free" }
      ]
    },
    {
      name: "PDF Tools",
      description: "Complete PDF editing, conversion & management suite",
      icon: <FileImage className="w-6 h-6" />,
      color: "bg-red-100 text-red-700",
      borderColor: "border-red-200",
      path: "/tools/design/pdf",
      tools: [
        // Convert to PDF - FREE
        { name: "Image to PDF", icon: <FileImage />, path: "/tools/design/pdf", tier: "free", popular: true },
        { name: "Text to PDF", icon: <FileText />, path: "/tools/design/pdf", tier: "free" },
        { name: "HTML to PDF", icon: <Code />, path: "/tools/design/pdf", tier: "free" },
        { name: "Multiple Images to PDF", icon: <Files />, path: "/tools/design/pdf", tier: "free" },
        // Organize - FREE
        { name: "Merge PDFs", icon: <Merge />, path: "/tools/design/pdf", tier: "free", popular: true },
        { name: "Split PDF", icon: <Scissors />, path: "/tools/design/pdf", tier: "free" },
        { name: "Rotate PDF", icon: <RotateCw />, path: "/tools/design/pdf", tier: "free" },
        { name: "Extract Pages", icon: <Copy />, path: "/tools/design/pdf", tier: "free" },
        { name: "Delete Pages", icon: <FileText />, path: "/tools/design/pdf", tier: "free" },
        { name: "Reorder Pages", icon: <Layers />, path: "/tools/design/pdf", tier: "free" },
        // Edit - PRO
        { name: "Add Text to PDF", icon: <Type />, path: "/tools/design/pdf", tier: "free", isNew: true },
        { name: "Add Highlight", icon: <Highlighter />, path: "/tools/design/pdf", tier: "free", isNew: true },
        { name: "Add Stamp", icon: <Stamp />, path: "/tools/design/pdf", tier: "free", isNew: true },
        { name: "Add Watermark", icon: <Droplet />, path: "/tools/design/pdf", tier: "free" },
        { name: "Add Page Numbers", icon: <Hash />, path: "/tools/design/pdf", tier: "free" },
        { name: "Crop PDF", icon: <Scissors />, path: "/tools/design/pdf", tier: "free" },
        { name: "Redact PDF", icon: <Eye />, path: "/tools/design/pdf", tier: "free" },
        // Security - PRO
        { name: "Protect PDF", icon: <Lock />, path: "/tools/design/pdf", tier: "free", popular: true },
        { name: "Unlock PDF", icon: <Unlock />, path: "/tools/design/pdf", tier: "free" },
        { name: "Flatten PDF", icon: <Layers />, path: "/tools/design/pdf", tier: "free" },
        // Optimize - FREE
        { name: "Compress PDF", icon: <Zap />, path: "/tools/design/pdf", tier: "free", popular: true },
        { name: "Repair PDF", icon: <Settings />, path: "/tools/design/pdf", tier: "free" },
        { name: "PDF Info", icon: <Eye />, path: "/tools/design/pdf", tier: "free" },
        { name: "Extract Text (OCR)", icon: <ScanText />, path: "/tools/design/pdf", tier: "free" },
        // AI Tools - PRO
        { name: "AI PDF Summarizer", icon: <Bot />, path: "/tools/design/pdf", tier: "pro", isNew: true },
        { name: "Chat with PDF", icon: <MessageSquare />, path: "/tools/design/pdf", tier: "pro", isNew: true },
        { name: "Translate PDF", icon: <Languages />, path: "/tools/design/pdf", tier: "pro" },
        { name: "AI Generate Questions", icon: <Sparkles />, path: "/tools/design/pdf", tier: "pro" },
        // Bulk - PRO
        { name: "Bulk PDF Processing", icon: <FolderOpen />, path: "/tools/design/pdf", tier: "pro", isNew: true }
      ]
    },
    {
      name: "SEO Tools",
      description: "Optimize your website for search engines",
      icon: <Search className="w-6 h-6" />,
      color: "bg-violet-100 text-violet-700",
      borderColor: "border-violet-200",
      path: "/tools/seo",
      tools: [
        { name: "SEO Dashboard", icon: <BarChart3 />, path: "/tools/seo/dashboard", tier: "pro", popular: true },
        { name: "Keyword Research", icon: <Target />, path: "/tools/seo", tier: "free", popular: true },
        { name: "Backlink Analyzer", icon: <Link2 />, path: "/tools/seo", tier: "pro", isDemo: true },
        { name: "Rank Tracking", icon: <TrendingUp />, path: "/tools/seo", tier: "pro", isDemo: true },
        { name: "Site Audit", icon: <Search />, path: "/tools/seo", tier: "pro" },
        { name: "Competitor Analysis", icon: <BarChart3 />, path: "/tools/seo", tier: "pro", isDemo: true }
      ]
    },
    {
      name: "Technical SEO",
      description: "Advanced technical SEO analysis tools",
      icon: <Settings className="w-6 h-6" />,
      color: "bg-indigo-100 text-indigo-700",
      borderColor: "border-indigo-200",
      path: "/tools/seo/technical",
      tools: [
        { name: "robots.txt Tester", icon: <FileCode />, path: "/tools/seo/technical", tier: "free" },
        { name: ".htaccess Tester", icon: <FileCode />, path: "/tools/seo/technical", tier: "free" },
        { name: "Sitemap Generator", icon: <Map />, path: "/tools/seo/technical", tier: "free", popular: true },
        { name: "RSS Feed Parser", icon: <Rss />, path: "/tools/seo/technical", tier: "free" },
        { name: "Fetch & Render", icon: <Globe />, path: "/tools/seo/technical", tier: "pro" },
        { name: "Mobile-Friendly Test", icon: <Smartphone />, path: "/tools/seo/technical", tier: "free", popular: true },
        { name: "Mobile-First Index", icon: <Smartphone />, path: "/tools/seo/technical", tier: "pro" },
        { name: "AMP Validator", icon: <Zap />, path: "/tools/seo/technical", tier: "free" },
        { name: "Hreflang Tester", icon: <Globe />, path: "/tools/seo/technical", tier: "free" },
        { name: "SERP Simulator", icon: <Search />, path: "/tools/seo/technical", tier: "free", popular: true },
        { name: "Schema Generator", icon: <Code />, path: "/tools/seo/technical", tier: "free" },
        { name: "Algo Update Tracker", icon: <History />, path: "/tools/seo/technical", tier: "free" },
        { name: "AI Bot Access Tester", icon: <Bot />, path: "/tools/seo/technical", tier: "free", isNew: true },
        { name: "Local Search Simulator", icon: <Map />, path: "/tools/seo/technical", tier: "pro" },
        { name: "Review Link Generator", icon: <Star />, path: "/tools/seo/technical", tier: "free" }
      ]
    },
    {
      name: "Content Marketing",
      description: "Create & optimize content that converts",
      icon: <FileText className="w-6 h-6" />,
      color: "bg-purple-100 text-purple-700",
      borderColor: "border-purple-200",
      path: "/tools/content",
      tools: [
        { name: "AI Content Writer", icon: <Wand2 />, path: "/tools/content", tier: "pro", popular: true },
        { name: "Plagiarism Checker", icon: <Copy />, path: "/tools/content", tier: "pro" },
        { name: "Content Rewriter", icon: <Sparkles />, path: "/tools/content", tier: "pro" },
        { name: "Readability Analyzer", icon: <BookOpen />, path: "/tools/content", tier: "free" },
        { name: "Topic Research", icon: <Target />, path: "/tools/content", tier: "free" },
        { name: "Content Calendar", icon: <Calendar />, path: "/tools/content", tier: "pro" },
        { name: "Blog Manager", icon: <PenTool />, path: "/blog", tier: "pro" },
        { name: "View Blogs", icon: <Eye />, path: "/blogs", tier: "free" }
      ]
    },
    {
      name: "Social Media",
      description: "Manage and grow your social presence",
      icon: <Share2 className="w-6 h-6" />,
      color: "bg-pink-100 text-pink-700",
      borderColor: "border-pink-200",
      path: "/tools/social-media",
      tools: [
        { name: "Hashtag Generator", icon: <Hash />, path: "/tools/social-media", tier: "free", popular: true },
        { name: "Post Scheduler", icon: <Calendar />, path: "/tools/social-media", tier: "pro" },
        { name: "Social Analytics", icon: <BarChart3 />, path: "/tools/social-media", tier: "pro" },
        { name: "AI Caption Writer", icon: <Wand2 />, path: "/tools/social-media", tier: "pro" },
        { name: "Trending Topics", icon: <TrendingUp />, path: "/tools/social-media", tier: "free" },
        { name: "Influencer Finder", icon: <Users />, path: "/tools/social-media", tier: "pro", isDemo: true },
        { name: "Instagram Tools", icon: <Instagram />, path: "/tools/social-media", tier: "free" },
        { name: "Twitter/X Tools", icon: <Twitter />, path: "/tools/social-media", tier: "free" },
        { name: "LinkedIn Tools", icon: <Linkedin />, path: "/tools/social-media", tier: "free" }
      ]
    },
    {
      name: "Design Tools",
      description: "Create stunning visuals for your brand",
      icon: <Palette className="w-6 h-6" />,
      color: "bg-orange-100 text-orange-700",
      borderColor: "border-orange-200",
      path: "/tools/design",
      tools: [
        { name: "Color Palette Generator", icon: <Droplet />, path: "/tools/design", tier: "free", popular: true },
        { name: "AI Thumbnail Studio", icon: <Sparkles />, path: "/tools/design/thumbnail-studio", tier: "free", popular: true, isNew: true },
        { name: "AI Logo Maker", icon: <Sparkles />, path: "/tools/design", tier: "pro", isDemo: true },
        { name: "Banner Creator", icon: <Layout />, path: "/tools/design", tier: "pro", isDemo: true },
        { name: "Thumbnail Designer", icon: <Image />, path: "/tools/design/thumbnail-studio", tier: "free" },
        { name: "Font Pairing", icon: <Type />, path: "/tools/design", tier: "free" },
        { name: "Mockup Generator", icon: <Layers />, path: "/tools/design", tier: "pro" }
      ]
    },
    {
      name: "Google Ads",
      description: "Write high-converting Google Ads copy with Claude Sonnet 4.5",
      icon: <Megaphone className="w-6 h-6" />,
      color: "bg-blue-100 text-blue-700",
      borderColor: "border-blue-200",
      path: "/tools/google-ads",
      tools: [
        { name: "AI Ad Copy Generator", icon: <Wand2 />, path: "/tools/google-ads", tier: "pro", popular: true, isNew: true },
        { name: "Keyword Planner", icon: <Target />, path: "/tools/google-ads", tier: "free" },
      ]
    },
    {
      name: "MarkeeMix Indic AI",
      description: "Made for India — speech, voice, translate, & LLM in 22 languages",
      icon: <Sparkles className="w-6 h-6" />,
      color: "bg-orange-100 text-orange-700",
      borderColor: "border-orange-200",
      path: "/tools/sarvam-studio",
      tools: [
        { name: "Indic AI Studio (All-in-one)", icon: <Sparkles />, path: "/tools/sarvam-studio", tier: "free", popular: true, isNew: true },
        { name: "🔴 Live Captions Studio", icon: <Zap />, path: "/tools/live-captions", tier: "free", popular: true, isNew: true },
        { name: "Indian Speech-to-Text", icon: <Zap />, path: "/tools/sarvam-studio", tier: "free", isNew: true },
        { name: "Indian Voice Studio", icon: <Wand2 />, path: "/tools/sarvam-studio", tier: "free", isNew: true },
        { name: "23-Language Translate", icon: <Globe />, path: "/tools/sarvam-studio", tier: "free", isNew: true },
        { name: "Language Identifier", icon: <ScanText />, path: "/tools/sarvam-studio", tier: "free", isNew: true },
        { name: "Roman ⇄ Devanagari Transliterator", icon: <Type />, path: "/tools/sarvam-studio", tier: "free", isNew: true },
        { name: "Indic AI Chat", icon: <PenTool />, path: "/tools/sarvam-studio", tier: "free", isNew: true },
      ]
    },
    {
      name: "MarkeeMix Regional Toolkit",
      description: "Ready-made India workflows — regional ads, WhatsApp broadcasts, podcast → blog, call analyzer",
      icon: <Megaphone className="w-6 h-6" />,
      color: "bg-rose-100 text-rose-700",
      borderColor: "border-rose-200",
      path: "/tools/regional-toolkit",
      tools: [
        { name: "Regional Ad Studio", icon: <Megaphone />, path: "/tools/regional-toolkit", tier: "free", popular: true, isNew: true },
        { name: "WhatsApp Broadcast Composer", icon: <PenTool />, path: "/tools/regional-toolkit", tier: "free", popular: true, isNew: true },
        { name: "Podcast → Blog Repurposer", icon: <FileText />, path: "/tools/regional-toolkit", tier: "free", isNew: true },
        { name: "Sales Call Analyzer", icon: <ScanText />, path: "/tools/regional-toolkit", tier: "free", isNew: true },
      ]
    },
    {
      name: "MarkeeMix Scale Toolkit",
      description: "Bulk workflows — 500 SEO metas, 1000 landing pages, and 10-language newsletters from one spreadsheet",
      icon: <Table className="w-6 h-6" />,
      color: "bg-emerald-100 text-emerald-700",
      borderColor: "border-emerald-200",
      path: "/tools/scale-toolkit",
      tools: [
        { name: "Bulk Meta Generator", icon: <ScanText />, path: "/tools/scale-toolkit", tier: "free", popular: true, isNew: true },
        { name: "Programmatic SEO Pages", icon: <Layers />, path: "/tools/scale-toolkit", tier: "free", popular: true, isNew: true },
        { name: "Regional Newsletter Studio", icon: <PenTool />, path: "/tools/scale-toolkit", tier: "free", isNew: true },
      ]
    },
    {
      name: "MarkeeMix Keyword Booster",
      description: "Frontier LLM at 20× lower cost — long-tail expansion, clustering & topic ideation",
      icon: <Zap className="w-6 h-6" />,
      color: "bg-indigo-100 text-indigo-700",
      borderColor: "border-indigo-200",
      path: "/tools/kimi-booster",
      tools: [
        { name: "Long-tail Keyword Expander", icon: <Zap />, path: "/tools/kimi-booster", tier: "free", popular: true, isNew: true },
        { name: "Keyword Clusterer", icon: <Layers />, path: "/tools/kimi-booster", tier: "free", isNew: true },
        { name: "Blog Topic Ideator", icon: <Wand2 />, path: "/tools/kimi-booster", tier: "free", isNew: true },
      ]
    }
  ];

  // Calculate stats
  const allTools = toolCategories.flatMap(cat => cat.tools);
  const freeTools = allTools.filter(t => t.tier === "free");
  const proTools = allTools.filter(t => t.tier === "pro");
  const newTools = allTools.filter(t => t.isNew);

  // Filter tools
  const getFilteredTools = (tools) => {
    let filtered = tools;
    
    if (filter === "free") {
      filtered = filtered.filter(t => t.tier === "free");
    } else if (filter === "pro") {
      filtered = filtered.filter(t => t.tier === "pro");
    }
    
    if (searchQuery) {
      filtered = filtered.filter(t => 
        t.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return filtered;
  };

  return (
    <div className="min-h-screen bg-white flex flex-col font-sans">
      <Header />

      <div className="flex-1">
        {/* Workflows banner — premium */}
        <section className="border-b border-slate-100 py-3 px-4 sm:px-6 lg:px-8 bg-[#FAF5FF]" data-testid="all-tools-workflows-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 text-sm">
            <p className="text-[#111111]">
              <Sparkles className="w-4 h-4 inline mr-1.5 -mt-0.5 text-[#A855F7]" />
              <strong>New here?</strong> Don&rsquo;t pick a tool — pick a goal. Our workflows run the right tools for you.
            </p>
            <Button size="sm" onClick={() => navigate("/workflows")} className="rounded-full bg-[#111111] hover:bg-black text-white whitespace-nowrap" data-testid="all-tools-workflows-cta">
              Start with a Workflow →
            </Button>
          </div>
        </section>

        {/* Hero — premium */}
        <section className="bg-white py-16 border-b border-slate-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-xs tracking-[0.18em] font-semibold text-[#A855F7] uppercase mb-3">Tools Directory</p>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#111111] mb-4" style={{ fontFamily: "'Outfit', 'Inter', system-ui, sans-serif" }}>
              All marketing tools in one place
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">
              {allTools.length}+ AI-powered tools for SEO, content, YouTube, design, ads, and PDFs. Pick a tool or search below.
            </p>

            {/* Stats — premium */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10 max-w-2xl mx-auto">
              <div className="p-4 rounded-2xl border border-slate-200 bg-white">
                <p className="text-3xl font-semibold text-[#111111]">{allTools.length}</p>
                <p className="text-xs text-slate-500 mt-1">Total tools</p>
              </div>
              <div className="p-4 rounded-2xl border border-[#A855F7]/20 bg-[#FAF5FF]">
                <p className="text-3xl font-semibold text-[#A855F7]">{freeTools.length}</p>
                <p className="text-xs text-slate-500 mt-1">Free tools</p>
              </div>
              <div className="p-4 rounded-2xl border border-slate-200 bg-white">
                <p className="text-3xl font-semibold text-[#111111]">{proTools.length}</p>
                <p className="text-xs text-slate-500 mt-1">Pro tools</p>
              </div>
              <div className="p-4 rounded-2xl border border-slate-200 bg-white">
                <p className="text-3xl font-semibold text-[#111111]">{newTools.length}</p>
                <p className="text-xs text-slate-500 mt-1">New tools</p>
              </div>
            </div>

            {/* Search & Filter — premium */}
            <div className="max-w-2xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search 75+ tools by name…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-full bg-white border border-slate-200 text-[#111111] placeholder:text-slate-400 focus:outline-none focus:border-[#A855F7] transition-colors"
                    data-testid="search-tools"
                  />
                </div>
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => setFilter("all")}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
                      filter === "all" ? "bg-[#111111] text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setFilter("free")}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
                      filter === "free" ? "bg-[#A855F7] text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    Free
                  </button>
                  <button
                    onClick={() => setFilter("pro")}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
                      filter === "pro" ? "bg-[#111111] text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <Crown className="w-3.5 h-3.5 inline mr-1" /> Pro
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tools Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="space-y-16">
            {toolCategories.map((category, index) => {
              const filteredTools = getFilteredTools(category.tools);
              if (filteredTools.length === 0) return null;

              return (
                <div key={category.name} className={index > 0 ? "pt-10 border-t border-slate-100" : ""}>
                  {/* Category Header — premium */}
                  <div
                    className="flex items-center gap-4 mb-8 cursor-pointer group"
                    onClick={() => navigate(category.path)}
                  >
                    <div className="w-14 h-14 rounded-2xl bg-[#111111] text-white flex items-center justify-center group-hover:bg-[#A855F7] transition-colors">
                      {category.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h2 className="text-2xl font-semibold text-[#111111] group-hover:text-[#A855F7] transition-colors" style={{ fontFamily: "'Outfit', 'Inter', system-ui, sans-serif" }}>
                          {category.name}
                        </h2>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                          {filteredTools.length} tools
                        </span>
                      </div>
                      <p className="text-slate-500 mt-1 text-sm">{category.description}</p>
                    </div>
                  </div>

                  {/* Tools Grid — premium */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {filteredTools.map((tool) => (
                      <div
                        key={tool.name}
                        className="p-4 rounded-2xl border border-slate-200 bg-white hover:border-[#A855F7]/40 hover:shadow-[0_8px_24px_rgba(168,85,247,0.08)] transition-all cursor-pointer group"
                        onClick={() => navigate(tool.path)}
                        data-testid={`tool-${tool.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="w-9 h-9 rounded-xl bg-[#FAF5FF] text-[#A855F7] flex items-center justify-center group-hover:bg-[#A855F7] group-hover:text-white transition-colors">
                            {tool.icon}
                          </div>
                          <div className="flex gap-1 flex-wrap justify-end">
                            {tool.isNew && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#A855F7] text-white">NEW</span>
                            )}
                            {tool.popular && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#111111] text-white flex items-center gap-0.5">
                                <Flame className="w-2.5 h-2.5" /> Hot
                              </span>
                            )}
                            {tool.isDemo && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800" title="Preview UI — real data source coming soon">DEMO</span>
                            )}
                            {tool.tier === "free" ? (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">FREE</span>
                            ) : (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-[#111111] flex items-center gap-0.5">
                                <Crown className="w-2.5 h-2.5" /> PRO
                              </span>
                            )}
                          </div>
                        </div>
                        <h3 className="text-sm font-semibold text-[#111111] group-hover:text-[#A855F7] transition-colors leading-tight">
                          {tool.name}
                        </h3>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA — premium flat black */}
        <section className="bg-[#111111] py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-xs tracking-[0.18em] font-semibold text-[#A855F7] uppercase mb-3">Pro Access</p>
            <h2 className="text-3xl sm:text-4xl font-semibold text-white mb-4" style={{ fontFamily: "'Outfit', 'Inter', system-ui, sans-serif" }}>
              Unlock all Pro tools
            </h2>
            <p className="text-slate-300 mb-8 text-base sm:text-lg max-w-xl mx-auto">
              Get unlimited access to {proTools.length}+ Pro tools with our premium plan. Cancel anytime.
            </p>
            <div className="flex justify-center gap-3 flex-wrap">
              <button
                onClick={() => navigate("/pricing")}
                className="px-6 py-3 rounded-full bg-white text-[#111111] font-medium hover:bg-slate-100 transition-colors inline-flex items-center gap-2"
              >
                <Crown className="w-4 h-4" /> View pricing
              </button>
              <button
                onClick={() => navigate("/tools/design/pdf")}
                className="px-6 py-3 rounded-full border border-white/20 text-white font-medium hover:bg-white/10 transition-colors"
              >
                Try free tools
              </button>
            </div>
          </div>
        </section>
      </div>

      <ToolSEOContent toolId="all-tools" />
      <Footer />
      <WhatsAppChat />
    </div>
  );
}
